import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, PageHeader, Select, StatusBadge } from '../components/ui';
import {
  issueReportService,
  type IssueReport,
  type IssueReportCategory,
  type IssueReportFilters,
  type IssueReportPriority,
  type IssueReportStatus,
  type IssueReportSummary,
} from '../services/issue-report.service';
import {
  ISSUE_REPORT_CATEGORY_OPTIONS,
  ISSUE_REPORT_PRIORITY_OPTIONS,
  ISSUE_REPORT_STATUS_OPTIONS,
  getIssueReportCategoryLabel,
  getIssueReportPriorityMeta,
  getIssueReportReporterLabel,
  getIssueReportStatusMeta,
} from '../lib/issueReports';

const inputClassName =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm transition-colors focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500';

interface ReviewFormState {
  status: IssueReportStatus;
  adminPriority: IssueReportPriority;
  adminNotes: string;
  resolutionSummary: string;
}

const emptyFilterValue = '';

export default function AdminIssueReportsPage() {
  const navigate = useNavigate();
  const [reports, setReports] = useState<IssueReport[]>([]);
  const [summary, setSummary] = useState<IssueReportSummary | null>(null);
  const [filters, setFilters] = useState<IssueReportFilters>({});
  const [draftFilters, setDraftFilters] = useState({
    search: '',
    status: emptyFilterValue,
    category: emptyFilterValue,
    priority: emptyFilterValue,
  });
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [reviewForm, setReviewForm] = useState<ReviewFormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const selectedReport = useMemo(
    () => reports.find((report) => report.id === selectedReportId) ?? null,
    [reports, selectedReportId],
  );

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [nextReports, nextSummary] = await Promise.all([
          issueReportService.getAdminIssueReports(filters),
          issueReportService.getIssueReportSummary(),
        ]);
        setReports(nextReports);
        setSummary(nextSummary);
      } catch (loadError: any) {
        setError(loadError.response?.data?.error || loadError.message || 'Failed to load issue report inbox');
      } finally {
        setLoading(false);
      }
    };

    void loadData();
  }, [filters]);

  useEffect(() => {
    if (reports.length === 0) {
      setSelectedReportId(null);
      return;
    }

    if (!selectedReportId || !reports.some((report) => report.id === selectedReportId)) {
      setSelectedReportId(reports[0].id);
    }
  }, [reports, selectedReportId]);

  useEffect(() => {
    if (!selectedReport) {
      setReviewForm(null);
      return;
    }

    setReviewForm({
      status: selectedReport.status,
      adminPriority: selectedReport.adminPriority,
      adminNotes: selectedReport.adminNotes || '',
      resolutionSummary: selectedReport.resolutionSummary || '',
    });
    setSuccessMessage(null);
  }, [selectedReport]);

  const handleApplyFilters = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setFilters({
      search: draftFilters.search.trim() || undefined,
      status: (draftFilters.status || undefined) as IssueReportStatus | undefined,
      category: (draftFilters.category || undefined) as IssueReportCategory | undefined,
      priority: (draftFilters.priority || undefined) as IssueReportPriority | undefined,
    });
  };

  const handleRefresh = () => {
    setFilters((previous) => ({ ...previous }));
  };

  const handleSaveReview = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedReportId || !reviewForm) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const updated = await issueReportService.updateIssueReport(selectedReportId, reviewForm);
      setReports((previous) => previous.map((report) => (report.id === updated.id ? updated : report)));
      setSummary(await issueReportService.getIssueReportSummary());
      setSuccessMessage('Issue report updated successfully.');
    } catch (saveError: any) {
      setError(saveError.response?.data?.error || saveError.message || 'Failed to save issue report review');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        title="Issue Inbox"
        description="Review manufacturer-reported bugs, workflow gaps, and feature requests. Prioritize them here so implementation can be planned cleanly."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => navigate('/admin')}>
              Back to Admin Dashboard
            </Button>
            <Button variant="outline" onClick={handleRefresh}>
              Refresh
            </Button>
          </div>
        }
      />

      {summary && (
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-4">
          <SummaryCard label="Total Reports" value={summary.total} toneClass="bg-slate-50 text-slate-700" />
          <SummaryCard label="Submitted" value={summary.byStatus.SUBMITTED} toneClass="bg-blue-50 text-blue-700" />
          <SummaryCard label="Under Review / Planned" value={summary.byStatus.UNDER_REVIEW + summary.byStatus.PLANNED} toneClass="bg-amber-50 text-amber-700" />
          <SummaryCard label="In Progress / Implemented" value={summary.byStatus.IN_PROGRESS + summary.byStatus.IMPLEMENTED} toneClass="bg-emerald-50 text-emerald-700" />
        </div>
      )}

      {(error || successMessage) && (
        <div className="mt-6 space-y-3">
          {error && <Alert variant="error">{error}</Alert>}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}
        </div>
      )}

      <form onSubmit={handleApplyFilters} className="mt-6 rounded-2xl bg-white p-5 shadow">
        <div className="grid gap-4 lg:grid-cols-[2fr_1fr_1fr_1fr_auto]">
          <div>
            <FieldLabel text="Search" />
            <input
              value={draftFilters.search}
              onChange={(event) => setDraftFilters((previous) => ({ ...previous, search: event.target.value }))}
              className={inputClassName}
              placeholder="Search by title, description, manufacturer, or reporter"
            />
          </div>
          <Select
            label="Status"
            value={draftFilters.status}
            options={ISSUE_REPORT_STATUS_OPTIONS}
            placeholder="All statuses"
            onChange={(event) => setDraftFilters((previous) => ({ ...previous, status: event.target.value }))}
          />
          <Select
            label="Category"
            value={draftFilters.category}
            options={ISSUE_REPORT_CATEGORY_OPTIONS}
            placeholder="All categories"
            onChange={(event) => setDraftFilters((previous) => ({ ...previous, category: event.target.value }))}
          />
          <Select
            label="Priority"
            value={draftFilters.priority}
            options={ISSUE_REPORT_PRIORITY_OPTIONS}
            placeholder="All priorities"
            onChange={(event) => setDraftFilters((previous) => ({ ...previous, priority: event.target.value }))}
          />
          <div className="flex items-end">
            <Button type="submit">Apply</Button>
          </div>
        </div>
      </form>

      <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1.25fr)_minmax(340px,0.95fr)]">
        <section className="rounded-2xl bg-white p-6 shadow">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Incoming Reports</h2>
              <p className="mt-1 text-sm text-gray-600">Newest submissions appear first. Select one to review and update.</p>
            </div>
            <span className="rounded-full bg-gray-100 px-3 py-1 text-sm font-semibold text-gray-700">
              {reports.length} shown
            </span>
          </div>

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((item) => (
                <div key={item} className="h-28 animate-pulse rounded-xl bg-gray-100" />
              ))}
            </div>
          ) : reports.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center">
              <p className="text-sm font-medium text-gray-700">No issue reports match these filters</p>
              <p className="mt-1 text-sm text-gray-500">Try clearing filters or wait for new manufacturer submissions.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {reports.map((report) => {
                const status = getIssueReportStatusMeta(report.status);
                const priority = getIssueReportPriorityMeta(report.adminPriority);
                const isSelected = report.id === selectedReportId;

                return (
                  <button
                    key={report.id}
                    type="button"
                    onClick={() => setSelectedReportId(report.id)}
                    className={`w-full rounded-2xl border p-5 text-left transition ${
                      isSelected ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusBadge tone={status.tone} size="sm">
                        {status.label}
                      </StatusBadge>
                      <StatusBadge tone={priority.tone} size="sm">
                        {priority.label}
                      </StatusBadge>
                      <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-gray-700">
                        {getIssueReportCategoryLabel(report.category)}
                      </span>
                    </div>

                    <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">{report.title}</h3>
                        <p className="mt-1 text-sm text-gray-600">
                          {report.manufacturer.name} • {report.reporter.fullName} ({getIssueReportReporterLabel(report.reporter.reporterType)})
                        </p>
                      </div>
                      <div className="text-right text-xs text-gray-500">
                        <p>Created {new Date(report.createdAt).toLocaleString()}</p>
                        <p className="mt-1">Updated {new Date(report.updatedAt).toLocaleString()}</p>
                      </div>
                    </div>

                    <p className="mt-3 line-clamp-3 text-sm text-gray-700">{report.description}</p>

                    {report.affectedArea && (
                      <p className="mt-3 text-xs font-medium uppercase tracking-wide text-gray-500">
                        Area: <span className="text-gray-700 normal-case">{report.affectedArea}</span>
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <aside className="rounded-2xl bg-white p-6 shadow">
          {!selectedReport || !reviewForm ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center">
              <p className="text-sm font-medium text-gray-700">Select a report to review</p>
              <p className="mt-1 text-sm text-gray-500">You can set status, priority, and implementation notes from here.</p>
            </div>
          ) : (
            <div className="space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Review Details</h2>
                <p className="mt-1 text-sm text-gray-600">Keep this updated so manufacturer teams know what is happening.</p>
              </div>

              <div className="rounded-2xl bg-gray-50 p-4">
                <h3 className="text-base font-semibold text-gray-900">{selectedReport.title}</h3>
                <p className="mt-2 whitespace-pre-wrap text-sm text-gray-700">{selectedReport.description}</p>
                <dl className="mt-4 space-y-3 text-sm">
                  <MetaItem label="Manufacturer" value={selectedReport.manufacturer.name} />
                  <MetaItem
                    label="Reporter"
                    value={`${selectedReport.reporter.fullName} (${getIssueReportReporterLabel(selectedReport.reporter.reporterType)})`}
                  />
                  <MetaItem label="Category" value={getIssueReportCategoryLabel(selectedReport.category)} />
                  <MetaItem label="Affected Area" value={selectedReport.affectedArea || 'Not specified'} />
                  <MetaItem label="Created" value={new Date(selectedReport.createdAt).toLocaleString()} />
                </dl>
              </div>

              {selectedReport.stepsToReproduce && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Steps to Reproduce</p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700">{selectedReport.stepsToReproduce}</p>
                </div>
              )}

              {selectedReport.expectedOutcome && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Expected Outcome</p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700">{selectedReport.expectedOutcome}</p>
                </div>
              )}

              <form className="space-y-4 border-t border-gray-200 pt-5" onSubmit={handleSaveReview}>
                <Select
                  label="Status"
                  value={reviewForm.status}
                  options={ISSUE_REPORT_STATUS_OPTIONS}
                  onChange={(event) =>
                    setReviewForm((previous) =>
                      previous
                        ? { ...previous, status: event.target.value as IssueReportStatus }
                        : previous,
                    )
                  }
                />

                <Select
                  label="Priority"
                  value={reviewForm.adminPriority}
                  options={ISSUE_REPORT_PRIORITY_OPTIONS}
                  onChange={(event) =>
                    setReviewForm((previous) =>
                      previous
                        ? { ...previous, adminPriority: event.target.value as IssueReportPriority }
                        : previous,
                    )
                  }
                />

                <div>
                  <FieldLabel text="Admin Notes" />
                  <textarea
                    rows={4}
                    value={reviewForm.adminNotes}
                    onChange={(event) =>
                      setReviewForm((previous) =>
                        previous ? { ...previous, adminNotes: event.target.value } : previous,
                      )
                    }
                    className={inputClassName}
                    placeholder="Internal prioritization or decision notes visible to manufacturer users"
                  />
                </div>

                <div>
                  <FieldLabel text="Resolution / Delivery Notes" />
                  <textarea
                    rows={4}
                    value={reviewForm.resolutionSummary}
                    onChange={(event) =>
                      setReviewForm((previous) =>
                        previous ? { ...previous, resolutionSummary: event.target.value } : previous,
                      )
                    }
                    className={inputClassName}
                    placeholder="Use this when the request is implemented, scheduled, or intentionally closed."
                  />
                </div>

                <Button type="submit" isLoading={saving}>
                  Save Review
                </Button>
              </form>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function FieldLabel({ text }: { text: string }) {
  return <label className="block text-sm font-medium text-gray-700 mb-1">{text}</label>;
}

function SummaryCard({ label, value, toneClass }: { label: string; value: number; toneClass: string }) {
  return (
    <div className="rounded-2xl bg-white p-5 shadow">
      <div className={`inline-flex rounded-lg px-3 py-1 text-xs font-semibold uppercase tracking-wide ${toneClass}`}>
        {label}
      </div>
      <p className="mt-3 text-3xl font-bold text-gray-900">{value}</p>
    </div>
  );
}

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-800">{value}</dd>
    </div>
  );
}
